import apiClient from '../axios';
import {
  CreateQuestionRequest,
  GetUnifiedQuestionListRequest,
  QuestionFavoriteRequest,
  UpdateQuestionRequest,
  VoteQuestionRequest,
} from '../types/Question.api.type';
import { VoteAnalysisResponse, VotingProfile, AiAnalysisResponse } from '../types/Vote.api.type';
import { QuestionV2 } from '../types/Question.type';
import { ISlice } from '../types/Page.type';

/**
 * 설문 관련 API 모음
 */
/* 설문 API 주소 */
const QUESTION_URL = () => '/api/question';
const VOTE_URL = () => '/api/vote';

export const questionApi = {
  /**
   * 설문 등록 API
   * @param params
   */
  createQuestion: (params: CreateQuestionRequest) => {
    return apiClient.post(`${QUESTION_URL()}/createQuestion`, params);
  },

  /**
   * 통합 설문 목록 조회 API
   * 모든 설문 리스트 화면에서 사용 (전체/내설문/참여/즐겨찾기)
   */
  getUnifiedQuestionList(params: GetUnifiedQuestionListRequest): Promise<ISlice<QuestionV2>> {
    return apiClient
      .get<{
        results: ISlice<QuestionV2>;
      }>(`${QUESTION_URL()}/list`, { params })
      .then((res) => res.data.results);
  },

  /**
   * 설문 투표 API
   *
   * - 투표 시, 설문ID와 선택한 옵션 ID를 전달
   */
  voteQuestion: (params: VoteQuestionRequest) => {
    return apiClient.post(`${VOTE_URL()}/voteQuestion`, params);
  },

  /**
   * 설문 상세정보 조회 API
   */
  getQuestionDetail: (questionId: string, fingerprint: string | null) => {
    const headers: { [key: string]: string } = {};
    if (fingerprint) {
      headers['X-Fingerprint'] = fingerprint;
    }
    return apiClient.get(`${QUESTION_URL()}/getQuestionDetail/${questionId}`, { headers });
  },

  /**
   * 설문 수정 API
   */
  updateQuestion: (questionId: string, params: UpdateQuestionRequest) => {
    return apiClient.put(`${QUESTION_URL()}/updateQuestion/${questionId}`, params);
  },

  /**
   * 설문 삭제 API
   *
   * - delyYn 플래그 처리를 통한 삭제
   */
  deleteQuestion: async (questionId: string): Promise<void> => {
    await apiClient.put(`${QUESTION_URL()}/deleteQuestion/${questionId}/delete`);
  },

  /**
   * 설문 리스트 조회 For 내 설문 API
   */
  getQuestionListForMyQuestion: (userId: string) => {
    return apiClient.get(`${QUESTION_URL()}/getQuestionListForMyQuestion/${userId}`);
  },

  /**
   * 설문 즐겨찾기 API
   */
  questionFavorite: (questionFavoriteParam: QuestionFavoriteRequest) => {
    return apiClient.post(`${QUESTION_URL()}/questionFavorite`, questionFavoriteParam);
  },

  /**
   * 설문 생성 쿨다운 체크 API
   */
  checkCooldown: (): Promise<{
    onCooldown: boolean;
    cooldownEnd: string | null;
    message: string | null;
    limitReached: boolean;
    activeCount: number;
    maxActive: number | null;
    limitMessage: string | null;
  }> => {
    return apiClient.get(`${QUESTION_URL()}/checkCooldown`).then((res) => res.data.results);
  },

  /**
   * 설문 투표 분석 API (NEW-10 + S6-23)
   * BE가 인증 토큰에서 userId를 추출하여 개인화 분석 포함
   */
  getVoteAnalysis: (questionId: string): Promise<VoteAnalysisResponse> => {
    return apiClient
      .get<{ results: VoteAnalysisResponse }>(`${VOTE_URL()}/getVoteAnalysis/${questionId}`)
      .then((res) => res.data.results);
  },

  /** 투표 성향 프로필 (S6-23) */
  getVotingProfile: (count: number = 20): Promise<VotingProfile> => {
    return apiClient
      .get<{ results: VotingProfile }>(`${VOTE_URL()}/getVotingProfile`, { params: { count } })
      .then((res) => res.data.results);
  },

  /** AI 분석 생성 (S6-23) */
  getAiAnalysis: (questionId: number): Promise<AiAnalysisResponse> => {
    return apiClient
      .post<{ results: AiAnalysisResponse }>(`${VOTE_URL()}/getAiAnalysis`, { questionId })
      .then((res) => res.data.results);
  },
};
